// ANGULAR
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { M2MService } from '@api-requests-services/m2m.service';
// MODELS
import { M2M } from '@models/m2m.model';

export enum Views {
  Home = 'home',
  M2Ms = 'apps',
  M2MDetails = 'appDetails',
  CreatingM2M = 'creatingApp',
  EditingM2M = 'editingApp',
}

@Component({
  selector: 'app-m2m',
  templateUrl: './m2m.component.html',
  styleUrl: './m2m.component.css',
  imports: [
    CommonModule,
    FormsModule
  ]
})
export class M2mComponent {
  allM2Ms: M2M[];
  m2ms: M2M[];
  newM2M: M2M | null = null;
  m2mSearched: M2M | null = null;
  m2mSelected: M2M | null = null;
  m2mNameToSearch: string;
  m2mToSearch: string;
  // Control de contenido html
  views = Views;
  m2mNotFound: boolean = false;
  isReadOnly: boolean = true;
  showPassword: boolean = false;
  showOldPassword: boolean = false;
  currentView: string = Views.Home;

  constructor(
    private alertService: AlertService,
    public globalService: GlobalService,
    private m2mService: M2MService,
    private title: Title
  ) {
    this.title.setTitle('Oráculo - M2M');
  }

  selectView(view: string, m2mSelected?: M2M): void {
    if (!!m2mSelected) { this.m2mSelected = m2mSelected; }
    this.currentView = view;

    switch (this.currentView) {
      case Views.Home:
        this.m2mSearched = null;
        this.m2mNameToSearch = '';
        this.m2mToSearch = '';
        this.isReadOnly = true;
        this.showPassword = false;
        this.showOldPassword = false;
        break;
      case Views.M2Ms:
        this.m2mToSearch = '';
        this.m2ms = this.allM2Ms;
        this.isReadOnly = true;
        this.showPassword = false;
        this.showOldPassword = false;
        break;
      case Views.M2MDetails:
        this.m2mToSearch = '';
        this.isReadOnly = true;
        this.showPassword = false;
        this.showOldPassword = false;
        if (!!this.m2mSelected) {
          this.newM2M = {
            nombre: this.m2mSelected.nombre,
            password: this.m2mSelected.password,
            fechaPassword: new Date(this.m2mSelected.fechaPassword),
            passwordOld: !!this.m2mSelected.passwordOld ? this.m2mSelected.passwordOld : undefined
          };
        }
        break;
      case Views.CreatingM2M:
        this.newM2M = {
          nombre: "",
          password: "",
          fechaPassword: new Date()
        };
        this.isReadOnly = true;
        this.showPassword = false;
        this.showOldPassword = false;
        break;
      case Views.EditingM2M:
        this.isReadOnly = false;
        this.showPassword = false;
        this.showOldPassword = false;
        break;
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text)
      .then(() => console.log('Texto copiado al portapapeles'))
      .catch(err => console.error('Error al copiar:', err));
  }

  getM2Ms(changeView: boolean, names?: string[]): void {
    this.m2mService.getM2Ms(names ? names : undefined).subscribe(
      m2ms => {
        if (m2ms) {
          // console.log('M2Ms: ', m2ms); 
          this.allM2Ms = m2ms;
          this.m2ms = this.allM2Ms;
          if (changeView) {
            this.currentView = Views.M2Ms;
          }
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting M2Ms:', error);
        }
      }
    );
  }

  createM2M(): void {
    if (!!this.newM2M) {
      // Actualizar fechaPassword por si se ha tardado en hacer el formulario
      this.newM2M = {...this.newM2M, fechaPassword: new Date()};

      this.m2mService.postCreateM2M(this.newM2M).subscribe(
        () => {
          this.m2mSelected = this.newM2M;
          this.getM2Ms(false);
          this.selectView(Views.M2MDetails);
          this.alertService.showAutoCloseMessage(`¡M2M creado!`, ``, "success", 1200);
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error creating M2M:', error);
          }
        }
      );
    }
  }

  getM2M(): void {
    if (this.m2mNameToSearch) {
      this.m2mService.getM2M(this.m2mNameToSearch).subscribe(
        m2m => {
          if (m2m) {
            // console.log('M2M: ', m2m);
            this.m2mNotFound = false;
            this.m2mSearched = m2m;
            this.allM2Ms = !!this.m2mSearched ? [this.m2mSearched] : [];
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            if (error.status === 404) {
              this.m2mNotFound = true;
            } else {
              this.m2mNotFound = false;
              this.alertService.swalError(error.error);
            }
            console.error('Error getting M2M:', error);
          }
        }
      );
    } else {
      this.m2mNotFound = true;
    }
  }

  editM2M(): void {
    this.alertService.showConfirmAlert(
      `Editar M2M`,
      `¿Seguro que quieres editar la M2M con nombre "${this.m2mSelected?.nombre ?? ''}"?`
    ).then((result) => {
      if (result.isConfirmed && !!this.m2mSelected && !!this.newM2M) {
        this.m2mService.putEditM2M(this.m2mSelected.nombre, this.newM2M).subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`¡M2M editado!`, ``, "success", 1200);
            this.getM2Ms(true);
            this.selectView(Views.M2Ms);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error editing M2M:', error);
            }
          }
        );
      }
    });
  }

  deleteM2M(): void {
    this.alertService.showConfirmAlert(
      `Eliminar M2M`,
      `¿Seguro que quieres eliminar el M2M con nombre "${this.m2mSelected?.nombre ?? ''}"?`
    ).then((result) => {
      if (result.isConfirmed && !!this.m2mSelected) {
        this.m2mService.deleteM2M(this.m2mSelected.nombre).subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`¡M2M borrado!`, ``, "success", 1200);
            this.getM2Ms(true);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error deleting M2M:', error);
            }
          }
        );
      }
    });
  }

  rotatePassword(): void {
    this.alertService.showInputAlert(
      "Nueva contraseña",
      "Si no se escribe una contraseña se generara automáticamente"
    ).then((result) => {
      if (result.isConfirmed && !!this.m2mSelected) {
        this.m2mService.postRotateM2MPassword(this.m2mSelected.nombre, result.value).subscribe(
          passwordInfo => {
            if (!!this.newM2M) {
              this.newM2M.password = passwordInfo.password;
              this.m2mSelected!.password = passwordInfo.password;
              this.newM2M.passwordOld = passwordInfo.passwordOld;
              this.m2mSelected!.passwordOld = passwordInfo.passwordOld;
              this.newM2M.fechaPassword = new Date(passwordInfo.fechaPassword);
              this.m2mSelected!.fechaPassword = new Date(passwordInfo.fechaPassword);
            }
            this.alertService.showAutoCloseMessage(`¡Contraseña cambiada!`, ``, "success", 1200);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error rotating Password:', error);
            }
          }
        );
      }
    });
  }

  revokeOldPassword(): void {
    if (!!this.newM2M?.passwordOld && !!this.m2mSelected) {
      this.m2mService.postRevokeM2MOldPassword(this.m2mSelected.nombre).subscribe(
        () => {
          this.newM2M!.passwordOld = undefined;
          this.m2mSelected!.passwordOld = undefined;
          this.alertService.showAutoCloseMessage(`¡Antigua contraseña revocada!`, ``, "success", 1200);
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error revoking Old password:', error);
          }
        }
      );
    } else {
      this.alertService.showAutoCloseMessage(`No hay PasswordOld`, ``, "error", 1200);
    }
  }

  changeShowPassword(): void { this.showPassword = !this.showPassword; }

  changeShowOldPassword(): void { if (this.newM2M?.passwordOld) { this.showOldPassword = !this.showOldPassword; } }

  onSearchChange(): void {
    this.m2ms = this.allM2Ms.filter(m2m => m2m.nombre.toLowerCase().includes(this.m2mToSearch.toLowerCase()));

    if (this.m2mToSearch === undefined || this.m2mToSearch === '' || this.m2mToSearch === ' ') {
      this.m2ms = this.allM2Ms;
    }
  }
}